import { Capacitor } from '@capacitor/core';
import { audioContextManager } from './audio-context.js';

export function isNative() {
    return Capacitor.isNativePlatform();
}

// Native bit-perfect playback path: only viable for Flacdium tracks (direct file URL, no HLS/DRM)
// while running inside the Capacitor shell with Bit-perfect enabled. Everything else keeps using
// the existing WebView <audio> element untouched.
export function isNativeBitPerfectPath(track) {
    return isNative() && audioContextManager.isBitPerfectActive() && !!track?.isFlacdium;
}

function plugin() {
    return Capacitor.Plugins.FlacdiumAudio;
}

export const nativeAudio = {
    async loadTrack(url) {
        return plugin().loadTrack({ url });
    },
    async play() {
        return plugin().play();
    },
    async pause() {
        return plugin().pause();
    },
    async seekTo(ms) {
        return plugin().seekTo({ ms: Math.round(ms) });
    },
    async stop() {
        return plugin().stop();
    },
    async getPosition() {
        return plugin().getPosition();
    },
    async getRouteInfo() {
        return plugin().getRouteInfo();
    },
    onTrackEnded(callback) {
        return plugin().addListener('trackEnded', callback);
    },
    onPlaybackError(callback) {
        return plugin().addListener('playbackError', callback);
    },
    onDeviceDisconnected(callback) {
        return plugin().addListener('deviceDisconnected', callback);
    },
};

// Reuses the SAME <audio> element (and every existing play/pause/timeupdate/mediaSession/seek-bar
// listener already wired to it) instead of a second UI surface — shadows currentTime/duration/
// paused/play/pause as own properties (which JS lets shadow the HTMLMediaElement prototype's
// accessors) so the native engine drives them without a duplicate network fetch of the same file
// just to keep a second <audio> element's playback state in sync.
export function attachNativeShadow(element) {
    if (element.__flacdiumNativeShadow) return element.__flacdiumNativeShadow;

    const state = { currentTime: 0, duration: 0, paused: true };
    const originalPlay = element.play.bind(element);
    const originalPause = element.pause.bind(element);
    let pollHandle = null;

    Object.defineProperty(element, 'currentTime', {
        configurable: true,
        get: () => state.currentTime,
        set: (value) => {
            state.currentTime = value;
            nativeAudio.seekTo(value * 1000).catch(() => {});
        },
    });
    Object.defineProperty(element, 'duration', {
        configurable: true,
        get: () => state.duration,
    });
    Object.defineProperty(element, 'paused', {
        configurable: true,
        get: () => state.paused,
    });

    element.play = () => {
        state.paused = false;
        nativeAudio.play().catch(() => {});
        element.dispatchEvent(new Event('play'));
        element.dispatchEvent(new Event('playing'));
        shadow.startPolling();
        return Promise.resolve();
    };
    element.pause = () => {
        state.paused = true;
        nativeAudio.pause().catch(() => {});
        element.dispatchEvent(new Event('pause'));
    };

    const shadow = {
        state,
        setDurationMs(ms) {
            state.duration = ms / 1000;
            element.dispatchEvent(new Event('loadedmetadata'));
        },
        startPolling() {
            if (pollHandle) return;
            pollHandle = setInterval(async () => {
                if (state.paused) return;
                try {
                    const { positionMs, durationMs } = await nativeAudio.getPosition();
                    state.currentTime = positionMs / 1000;
                    if (durationMs) state.duration = durationMs / 1000;
                    element.dispatchEvent(new Event('timeupdate'));
                } catch {
                    // transient plugin-call failure — next tick retries
                }
            }, 500);
        },
        detach() {
            if (pollHandle) clearInterval(pollHandle);
            pollHandle = null;
            delete element.currentTime;
            delete element.duration;
            delete element.paused;
            element.play = originalPlay;
            element.pause = originalPause;
            delete element.__flacdiumNativeShadow;
        },
    };
    element.__flacdiumNativeShadow = shadow;
    return shadow;
}

export function detachNativeShadow(element) {
    element.__flacdiumNativeShadow?.detach();
}
